<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3.5">
    <div class="flex items-center gap-1.5">
      <p class="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{{ label }}</p>
      <!-- Every number here is a judgement call dressed as a fact. The
           definition travels with the number rather than living in a doc
           nobody opens. -->
      <Tooltip :text="definition">
        <span class="text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500 transition-colors cursor-help" role="img" :aria-label="definition">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9"/><path stroke-linecap="round" d="M12 16v-4M12 8h.01"/>
          </svg>
        </span>
      </Tooltip>
    </div>

    <div v-if="loading" class="animate-pulse mt-2">
      <div class="h-7 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div class="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded mt-2"></div>
    </div>

    <template v-else>
      <p class="mt-1.5 text-2xl font-bold text-gray-900 dark:text-white tabular-nums leading-none">{{ display }}</p>

      <p v-if="asOfNow" class="mt-2 text-[11px] text-gray-400 dark:text-gray-500">as of now</p>
      <p v-else-if="delta === null" class="mt-2 text-[11px] text-gray-400 dark:text-gray-500">No comparison available</p>
      <p v-else class="mt-2 flex items-center gap-1 text-[11px] font-medium" :class="deltaClass">
        <svg v-if="delta !== 0" class="w-3 h-3 flex-shrink-0" :class="delta > 0 ? '' : 'rotate-180'" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M10 4l6 8H4z"/>
        </svg>
        <span>{{ deltaLabel }}</span>
      </p>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Tooltip from 'controllers/vue_components/shared/Tooltip.vue'

const props = defineProps({
  label: { type: String, required: true },
  definition: { type: String, required: true },
  metric: { type: Object, default: null },   // { value, previous }
  format: { type: String, default: 'number' }, // number | percent | duration
  loading: { type: Boolean, default: false },
  asOfNow: { type: Boolean, default: false },
  // Whether a rise is good. Overdue going up is not a win.
  higherIsBetter: { type: Boolean, default: true },
})

function formatValue(value) {
  if (value === null || value === undefined) return '--'
  if (props.format === 'percent') return `${Math.round(value * 100)}%`
  if (props.format === 'duration') {
    if (value < 1) return `${Math.round(value * 60)}m`
    if (value < 48) return `${value.toFixed(1)}h`
    return `${(value / 24).toFixed(1)}d`
  }
  return String(value)
}

const display = computed(() => formatValue(props.metric?.value))

// Null rather than zero when there is nothing to compare against: an account
// with no history should not be told it is flat, which implies a measurement
// that was never taken.
const delta = computed(() => {
  const current = props.metric?.value
  const previous = props.metric?.previous
  if (current === null || current === undefined) return null
  if (previous === null || previous === undefined || previous === 0) return null
  return (current - previous) / Math.abs(previous)
})

const deltaLabel = computed(() => {
  if (delta.value === null) return ''
  const pct = Math.abs(Math.round(delta.value * 100))
  const direction = delta.value > 0 ? 'up' : delta.value < 0 ? 'down' : 'level'
  return direction === 'level' ? 'No change' : `${pct}% ${direction} on the previous period`
})

const deltaClass = computed(() => {
  if (!delta.value) return 'text-gray-400 dark:text-gray-500'
  const good = props.higherIsBetter ? delta.value > 0 : delta.value < 0
  return good ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
})
</script>
